import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import toastr from 'toastr';

export type PortalToastrLevel = 'success' | 'info' | 'warning' | 'error';
export type PortalToastrPosition = 'top-right' | 'bottom-right';

export interface PortalToastrAction {
  label: string;
  handler: () => void;
  variant?: 'primary' | 'danger' | 'light';
}

export type PortalToastrProgressStatus = 'pending' | 'running' | 'ok' | 'fail' | 'skip';

export interface PortalToastrProgressStep {
  id: string;
  label: string;
  status: PortalToastrProgressStatus;
  detail?: string;
}

export interface PortalToastrProgressHandle {
  readonly id: string;
  update(
    steps: PortalToastrProgressStep[],
    options?: { title?: string; message?: string; level?: PortalToastrLevel }
  ): void;
  succeed(message?: string | null, steps?: PortalToastrProgressStep[]): void;
  fail(message?: string | null, steps?: PortalToastrProgressStep[]): void;
  dismiss(): void;
}

export interface PortalToastrOptions {
  level?: PortalToastrLevel;
  urgent?: boolean;
  action?: PortalToastrAction;
  actions?: PortalToastrAction[];
  timeOut?: number;
  tapToDismiss?: boolean;
  preventDuplicateKey?: string;
  position?: PortalToastrPosition;
}

@Injectable({ providedIn: 'root' })
export class PortalToastrService {
  private readonly shownKeys = new Set<string>();
  private readonly progressToasts = new Map<string, {
    element: HTMLElement | null;
    native: unknown | null;
    title: string;
    message: string;
    steps: PortalToastrProgressStep[];
    level: PortalToastrLevel;
    actions: PortalToastrAction[];
  }>();
  private configured = false;
  private progressSeq = 0;

  constructor(private translate: TranslateService) {
    this.ensureDefaults();
  }

  success(message: string, options: PortalToastrOptions = {}): void {
    this.show(this.translate.instant('COMMON.SUCCESS'), message, {
      ...options,
      level: 'success',
      timeOut: options.timeOut ?? 4000,
    });
  }

  error(message: string, options: PortalToastrOptions = {}): void {
    this.show(this.translate.instant('COMMON.ERROR'), message, {
      ...options,
      level: 'error',
      timeOut: options.timeOut ?? 6000,
    });
  }

  info(message: string, options: PortalToastrOptions = {}): void {
    this.show(this.translate.instant('COMMON.INFO'), message, {
      ...options,
      level: 'info',
      timeOut: options.timeOut ?? 5000,
    });
  }

  warning(message: string, options: PortalToastrOptions = {}): void {
    this.show(this.translate.instant('COMMON.WARNING'), message, {
      ...options,
      level: 'warning',
      timeOut: options.timeOut ?? 5000,
    });
  }

  errorFrom(err: unknown, fallbackKey = 'COMMON.SAVE_FAILED'): void {
    this.error(this.messageFromError(err, this.translate.instant(fallbackKey)));
  }

  messageFromError(err: unknown, fallback: string): string {
    const body = err as {
      error?: { error?: string; message?: string };
      message?: string;
    } | null;
    const fromApi = body?.error?.error || body?.error?.message || body?.message;
    return (fromApi && String(fromApi).trim()) || fallback;
  }

  /**
   * Sticky toast that lists action steps and can be updated in place.
   * Call succeed()/fail() when the run finishes (auto-dismisses).
   */
  /**
   * Sticky toast with one or more time-critical buttons (approve / open / later).
   * Stays until the user picks an action or dismisses it.
   */
  choice(
    title: string,
    message: string,
    actions: PortalToastrAction[],
    options: {
      level?: PortalToastrLevel;
      timeOut?: number;
      preventDuplicateKey?: string;
    } = {}
  ): void {
    this.show(title, message, {
      level: options.level ?? 'warning',
      urgent: options.timeOut == null,
      timeOut: options.timeOut,
      tapToDismiss: false,
      preventDuplicateKey: options.preventDuplicateKey,
      actions,
    });
  }

  startProgress(
    title: string,
    steps: PortalToastrProgressStep[],
    options: {
      message?: string;
      position?: PortalToastrPosition;
      timeOut?: number;
      actions?: PortalToastrAction[];
    } = {}
  ): PortalToastrProgressHandle {
    this.ensureDefaults();
    const id = `progress-${++this.progressSeq}`;
    const initialSteps = steps.map((step) => ({ ...step }));
    const message = options.message ?? '';
    const actions = (options.actions ?? []).map((action) => ({ ...action }));
    const body = this.renderProgressBody(message, initialSteps, actions);
    this.progressToasts.set(id, {
      element: null,
      native: null,
      title,
      message,
      steps: initialSteps,
      level: 'info',
      actions,
    });
    const native = this.invoke('info', body, title, {
      timeOut: 0,
      extendedTimeOut: 0,
      tapToDismiss: false,
      escapeHtml: false,
      closeButton: true,
      positionClass: options.position === 'bottom-right' ? 'toast-bottom-right' : 'toast-top-right',
      toastClass: 'toastr portal-toastr-progress-toast',
      onShown: (toastElement: unknown) => {
        const current = this.progressToasts.get(id);
        if (!current) {
          return;
        }
        const el = this.toastElementFromResult(toastElement) || this.findProgressToastEl(id);
        if (el) {
          el.setAttribute('data-portal-toastr-progress', id);
          current.element = el;
        }
        current.native = native;
        this.paintProgress(id);
        this.bindProgressActions(id);
      },
      onHidden: () => {
        this.progressToasts.delete(id);
      },
    });
    const created = this.progressToasts.get(id);
    if (created) {
      created.native = native;
      created.element = this.toastElementFromResult(native);
      if (created.element) {
        created.element.setAttribute('data-portal-toastr-progress', id);
      }
    }
    this.paintProgress(id);
    this.bindProgressActions(id);

    const paint = (): void => {
      this.paintProgress(id);
    };
    const update = (
      nextSteps: PortalToastrProgressStep[],
      nextOptions?: { title?: string; message?: string; level?: PortalToastrLevel }
    ): void => {
      const current = this.progressToasts.get(id);
      if (!current) {
        return;
      }
      current.steps = nextSteps.map((step) => ({ ...step }));
      if (nextOptions?.title != null) {
        current.title = nextOptions.title;
      }
      if (nextOptions?.message != null) {
        current.message = nextOptions.message;
      }
      if (nextOptions?.level) {
        current.level = nextOptions.level;
      }
      paint();
      requestAnimationFrame(() => {
        this.paintProgress(id);
        this.bindProgressActions(id);
      });
    };
    const finish = (
      level: 'success' | 'error',
      doneMessage: string | null | undefined,
      nextSteps: PortalToastrProgressStep[] | undefined,
      timeOut: number
    ): void => {
      const current = this.progressToasts.get(id);
      if (!current) {
        return;
      }
      if (nextSteps) {
        current.steps = nextSteps.map((step) => ({ ...step }));
      }
      if (doneMessage != null) {
        current.message = doneMessage;
      }
      current.level = level;
      paint();
      window.setTimeout(() => this.dismissProgress(id), timeOut);
    };

    return {
      id,
      update,
      succeed: (doneMessage, nextSteps) =>
        finish('success', doneMessage, nextSteps, options.timeOut ?? 4000),
      fail: (doneMessage, nextSteps) =>
        finish('error', doneMessage, nextSteps, options.timeOut ?? 6000),
      dismiss: () => this.dismissProgress(id),
    };
  }

  show(title: string, message: string, options: PortalToastrOptions = {}): void {
    this.ensureDefaults();

    const key = options.preventDuplicateKey;
    if (key && this.shownKeys.has(key)) {
      return;
    }
    if (key) {
      this.shownKeys.add(key);
    }

    const level = options.level ?? 'info';
    const urgent = options.urgent === true;
    const actions = options.actions?.length
      ? options.actions
      : (options.action ? [options.action] : []);
    const timeOut = urgent ? 0 : (options.timeOut ?? 5000);
    const tapToDismiss = urgent ? false : (options.tapToDismiss ?? true);
    const position = options.position ?? (urgent ? 'bottom-right' : 'top-right');

    let body = message;
    if (actions.length > 0) {
      body += this.renderActionsHtml(actions, urgent ? 'danger' : 'primary');
    }

    const toastOptions: Record<string, unknown> = {
      timeOut,
      extendedTimeOut: urgent ? 0 : 1000,
      tapToDismiss,
      escapeHtml: false,
      positionClass: position === 'bottom-right' ? 'toast-bottom-right' : 'toast-top-right',
      onHidden: () => {
        if (key) {
          this.shownKeys.delete(key);
        }
      },
    };

    let nativeToast: unknown = null;
    if (actions.length > 0) {
      // Toastr invokes onShown as an animation callback without arguments.
      toastOptions.onShown = () => {
        this.bindActions(this.toastElementFromResult(nativeToast), actions, nativeToast);
      };
    }

    const toastClasses = ['toastr'];
    if (urgent) {
      toastClasses.push('portal-toastr-urgent');
    }
    if (actions.length > 0) {
      toastClasses.push('portal-toastr-with-actions');
    }
    if (toastClasses.length > 1) {
      toastOptions.toastClass = toastClasses.join(' ');
    }

    nativeToast = this.invoke(level, body, title, toastOptions as Record<string, unknown>);
    if (actions.length > 0) {
      // Bind immediately as well, so actions work before the show animation completes.
      this.bindActions(this.toastElementFromResult(nativeToast), actions, nativeToast);
    }
  }

  private ensureDefaults(): void {
    if (this.configured) {
      return;
    }
    toastr.options = {
      closeButton: true,
      debug: false,
      newestOnTop: true,
      progressBar: false,
      positionClass: 'toast-top-right',
      preventDuplicates: false,
      showDuration: 300,
      hideDuration: 1000,
      timeOut: 5000,
      extendedTimeOut: 1000,
      showEasing: 'swing',
      hideEasing: 'linear',
      showMethod: 'fadeIn',
      hideMethod: 'fadeOut',
      tapToDismiss: true,
      toastClass: 'toastr',
    };
    this.configured = true;
  }

  private invoke(level: PortalToastrLevel, message: string, title: string, options: Record<string, unknown>): unknown {
    switch (level) {
      case 'success':
        return toastr.success(message, title, options);
      case 'warning':
        return toastr.warning(message, title, options);
      case 'error':
        return toastr.error(message, title, options);
      default:
        return toastr.info(message, title, options);
    }
  }

  private updateProgress(
    id: string,
    steps: PortalToastrProgressStep[],
    options?: { title?: string; message?: string; level?: PortalToastrLevel }
  ): void {
    const current = this.progressToasts.get(id);
    if (!current) {
      return;
    }
    current.steps = steps.map((step) => ({ ...step }));
    if (options?.title != null) {
      current.title = options.title;
    }
    if (options?.message != null) {
      current.message = options.message;
    }
    if (options?.level) {
      current.level = options.level;
    }
    this.paintProgress(id);
  }

  private finishProgress(
    id: string,
    level: 'success' | 'error',
    message: string | undefined,
    steps: PortalToastrProgressStep[] | undefined,
    timeOut: number
  ): void {
    const current = this.progressToasts.get(id);
    if (!current) {
      return;
    }
    if (steps) {
      current.steps = steps.map((step) => ({ ...step }));
    }
    if (message != null) {
      current.message = message;
    }
    current.level = level;
    this.paintProgress(id);
    window.setTimeout(() => this.dismissProgress(id), timeOut);
  }

  private dismissProgress(id: string): void {
    const current = this.progressToasts.get(id);
    if (!current) {
      return;
    }
    this.progressToasts.delete(id);
    const native = current.native as { remove?: () => void } | null;
    if (native && typeof (toastr as { clear?: (toast?: unknown) => void }).clear === 'function') {
      (toastr as { clear: (toast?: unknown) => void }).clear(current.native);
      return;
    }
    current.element?.remove();
  }

  private paintProgress(id: string): void {
    const current = this.progressToasts.get(id);
    if (!current) {
      return;
    }
    let root = current.element;
    if (!root || !root.isConnected) {
      root = this.toastElementFromResult(current.native) || this.findProgressToastEl(id);
      current.element = root;
    }
    if (!root) {
      return;
    }
    root.setAttribute('data-portal-toastr-progress', id);
    root.classList.remove(
      'toastr-success',
      'toastr-info',
      'toastr-warning',
      'toastr-error',
      'toast-success',
      'toast-info',
      'toast-warning',
      'toast-error'
    );
    root.classList.add(`toastr-${current.level}`, `toast-${current.level}`);
    const titleEl = root.querySelector<HTMLElement>('.toastr-title, .toast-title');
    const messageEl = root.querySelector<HTMLElement>('.toastr-message, .toast-message');
    const bodyHtml = this.renderProgressBody(current.message, current.steps, current.actions);
    if (titleEl) {
      titleEl.textContent = current.title;
    }
    if (messageEl) {
      messageEl.innerHTML = bodyHtml;
      this.bindProgressActions(id);
      return;
    }
    const existing = root.querySelector('.portal-toastr-progress');
    if (existing) {
      existing.outerHTML = bodyHtml;
    }
    this.bindProgressActions(id);
  }

  private bindProgressActions(id: string): void {
    const current = this.progressToasts.get(id);
    const root = current?.element;
    if (!current || !root) {
      return;
    }
    root.querySelectorAll<HTMLButtonElement>('.portal-toastr-action').forEach((button) => {
      if (button.dataset.portalBound === '1') {
        return;
      }
      button.dataset.portalBound = '1';
      const label = button.getAttribute('data-portal-toastr-action') ?? '';
      const action = current.actions.find((item) => item.label === label);
      if (!action) {
        return;
      }
      button.addEventListener('click', (clickEvent) => {
        clickEvent.preventDefault();
        clickEvent.stopPropagation();
        action.handler();
      });
    });
  }

  private findProgressToastEl(id: string): HTMLElement | null {
    const marked = document.querySelector<HTMLElement>(`[data-portal-toastr-progress="${id}"]`);
    if (marked) {
      return marked;
    }
    const nodes = document.querySelectorAll<HTMLElement>('#toast-container .portal-toastr-progress-toast, #toast-container .toastr');
    return nodes.length ? nodes[nodes.length - 1] : null;
  }

  private renderProgressBody(
    message: string,
    steps: PortalToastrProgressStep[],
    actions: PortalToastrAction[] = []
  ): string {
    const done = steps.filter((step) => step.status === 'ok' || step.status === 'fail' || step.status === 'skip').length;
    const total = steps.length;
    const percent = total ? Math.round((done / total) * 100) : 0;
    const running = steps.find((step) => step.status === 'running');
    const bits: string[] = [];
    if (message) {
      bits.push(`<p class="portal-toastr-progress__message">${this.escapeHtml(message)}</p>`);
    }
    if (total) {
      bits.push(
        `<div class="portal-toastr-progress__bar" role="progressbar" aria-valuemin="0" aria-valuemax="100" aria-valuenow="${percent}">` +
          `<span style="width:${percent}%"></span>` +
        `</div>`
      );
      if (running) {
        bits.push(
          `<p class="portal-toastr-progress__current">${this.escapeHtml(running.label)}</p>`
        );
      }
      bits.push('<ul class="portal-toastr-progress__steps">');
      for (const step of steps) {
        const detail = step.detail ? `<small>${this.escapeHtml(step.detail)}</small>` : '';
        bits.push(
          `<li class="portal-toastr-progress__step portal-toastr-progress__step--${step.status}">` +
            `<span class="portal-toastr-progress__mark" aria-hidden="true"></span>` +
            `<span class="portal-toastr-progress__copy"><strong>${this.escapeHtml(step.label)}</strong>${detail}</span>` +
          `</li>`
        );
      }
      bits.push('</ul>');
    }
    if (actions.length) {
      bits.push(this.renderActionsHtml(actions, 'primary'));
    }
    return `<div class="portal-toastr-progress">${bits.join('')}</div>`;
  }

  private toastElementFromResult(result: unknown): HTMLElement | null {
    if (result instanceof HTMLElement) {
      return result;
    }
    if (Array.isArray(result) && result[0] instanceof HTMLElement) {
      return result[0];
    }
    if (result && typeof result === 'object') {
      const row = result as { 0?: unknown; get?: (index: number) => unknown };
      if (row[0] instanceof HTMLElement) {
        return row[0];
      }
      if (typeof row.get === 'function') {
        const el = row.get(0);
        if (el instanceof HTMLElement) {
          return el;
        }
      }
    }
    return null;
  }

  private renderActionsHtml(
    actions: PortalToastrAction[],
    defaultVariant: NonNullable<PortalToastrAction['variant']>,
  ): string {
    let html = '<div class="portal-toastr-actions">';
    for (const action of actions) {
      const variant = action.variant ?? defaultVariant;
      html +=
        `<button type="button" class="portal-toastr-action portal-toastr-action--${variant}"` +
        ` data-portal-toastr-action="${this.escapeHtml(action.label)}">` +
        `${this.escapeHtml(action.label)}</button>`;
    }
    html += '</div>';
    return html;
  }

  private bindActions(
    root: HTMLElement | null,
    actions: PortalToastrAction[],
    nativeToast: unknown,
  ): void {
    if (!root) {
      return;
    }
    root.querySelectorAll<HTMLButtonElement>('.portal-toastr-action').forEach((button) => {
      if (button.dataset.portalBound === '1') {
        return;
      }
      const label = button.getAttribute('data-portal-toastr-action') ?? '';
      const action = actions.find((item) => item.label === label);
      if (!action) {
        return;
      }
      button.dataset.portalBound = '1';
      button.addEventListener('click', (clickEvent) => {
        clickEvent.preventDefault();
        clickEvent.stopPropagation();
        action.handler();
        if (root.isConnected) {
          (toastr as { clear?: (toast?: unknown) => void }).clear?.(nativeToast);
          root.remove();
        }
      });
    });
  }

  private escapeHtml(value: string): string {
    return value
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }
}
